import React from 'react';
import { Container, Header, Sidebar, Content, Footer, Button } from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { formatPrice, getItemEffectivePrice } from '../../services/utilService';
import { scrollToTop } from '../../utils/scrollUtils';
import CampCart from './CampCart';
import CampSidebar from './CampSidebar';
import CampTopbar from './CampTopbar';

const CampLayout = ({ children, page, step, onRemoveItem }) => {
    const { cartItems: cart } = useCart();
    const [isMenu, setIsMenu] = React.useState(false);
    const navigate = useNavigate();
    const classNames = (...classes) => {
        return classes.filter(Boolean).join(' ');
    }

    // Below the xl breakpoint the cart panel is replaced by a sticky bottom bar.
    // The panel stays visible when the cart is empty (it shows an empty state);
    // the bottom bar only appears once there is something to review.
    const hasCartPanel = page === 'book' && step === 1;
    const hasCartBar = hasCartPanel && cart.length > 0;
    const cartTotal = cart.reduce((total, item) => total + getItemEffectivePrice(item), 0);

    // Flag the body while a sticky bottom bar is on screen (cart bar on the
    // booking step, action footer on review) so bottom-anchored toasts are
    // offset above it instead of covering its primary CTA (see index.css).
    const hasBottomBar = hasCartBar || (page === 'book' && step === 2);
    React.useEffect(() => {
        document.body.classList.toggle('has-sticky-bottom-bar', hasBottomBar);
        return () => document.body.classList.remove('has-sticky-bottom-bar');
    }, [hasBottomBar]);

    return (
        <Container className="min-h-screen flex flex-col">
            <Header className="sticky top-0 z-20 bg-white">
                <CampTopbar cart={cart} setIsMenu={setIsMenu} />
            </Header>
            <Container className="flex-1 flex">
                <Sidebar
                    className={classNames(
                        'lg:w-260 lg:block lg:sticky lg:top-20 lg:h-screen',
                        isMenu ? 'block fixed bottom-0 h-full z-10 bg-white' : 'hidden',
                    )}
                    style={{
                        borderRight: '1px solid #DCDCDC',
                        ...(isMenu ? { top: '70px', height: 'calc(100vh - 70px)' } : {}),
                        ...(!isMenu ? { top: '80px', height: 'calc(100vh - 80px)' } : {})
                    }}
                >
                    <CampSidebar isMenu={isMenu} setIsMenu={setIsMenu} page={page} />
                </Sidebar>

                {/* Main Content */}
                <Content
                    className={classNames('flex-1 overflow-auto', hasCartBar && 'pb-24 xl:pb-0')}
                    style={{ paddingTop: 30 }}
                >
                    {children}
                </Content>

                {/* Sticky Sidebar for CampCart (desktop only) */}
                <Sidebar
                    className="hidden xl:block"
                    style={{
                        borderLeft: '1px solid #DCDCDC',
                        position: 'relative', // Ensure Sidebar is positioned for sticky behavior
                    }}
                    width={hasCartPanel ? 270 : 0}
                    hidden={!hasCartPanel}
                >
                    <div
                        style={{
                            position: 'sticky',
                            top: '80px', // Account for sticky header height (~70px) + 10px padding
                            height: 'calc(100vh - 80px)', // Adjust height to account for header
                            maxHeight: 'calc(100vh - 80px)', // Prevent overflow
                            overflowY: 'auto', // Allow scrolling inside the sticky container if content overflows
                        }}
                    >
                        <CampCart cart={cart} onRemoveItem={onRemoveItem} />
                    </div>
                </Sidebar>
            </Container>

            {/* Sticky bottom cart bar: total + Review Cart (tablet/mobile) */}
            {hasCartBar && (
                <div className="cart-bottom-bar">
                    <div>
                        <div className="text-xs text-gray-500">
                            {cart.length} item{cart.length !== 1 ? 's' : ''} in cart
                        </div>
                        <div className="text-lg font-bold text-gray-900 leading-tight">
                            ${formatPrice(cartTotal)}<span className="currency-suffix">SGD</span>
                        </div>
                    </div>
                    <Button
                        appearance="primary"
                        size="lg"
                        onClick={() => {
                            scrollToTop();
                            navigate('/review');
                        }}
                    >
                        Review Cart
                    </Button>
                </div>
            )}
            <Footer />
        </Container>
    );
}

export default CampLayout;
